import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { useIsOrganisationAdmin } from '@hooks'
import { AxiosError } from 'axios'
import { Box, Paper, Stack, TableContainer, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { getCustomerFiles, getDefaultPlanTemplate } from '@queries'
import { downLoadFile } from '@utils'
import { deleteCustomerFile, saveCustomerFile, updateCustomerFileContent } from '@mutations'
import DocumentationTemplatesSection from './components/DocumentationTemplatesSection'
import PlanTemplateTable from './components/PlanTemplateTable'
import {
  orientationLabels,
  planTemplateTypes,
  planTypeSectionLabels,
  planTypeSingularLabels,
} from './constants'
import { PlanTemplateConfiguration, findCustomPlanTemplate } from './utils'

const planTemplateKind = 'pdfPlanTemplate'

const PlanTemplateManager = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  // A member who does not administer the organisation may download the
  // templates but not replace them.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isOrganisationAdmin

  const templatesQueryKey = getCustomerFiles.getKey(planTemplateKind)

  const { data: templatesResult, isLoading: isLoadingTemplates } = useQuery<
    CustomerFilesResult<PlanTemplateAdditionalData>
  >(templatesQueryKey, () => getCustomerFiles.request(planTemplateKind), {
    onError: (error: unknown) => {
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Planvorlagen'),
        {
          variant: 'error',
        },
      )
    },
  })

  const customTemplates = templatesResult?.files || []

  const templateFileName = ({ planType, paperFormat, orientation }: PlanTemplateConfiguration) =>
    `Planvorlage ${planTypeSingularLabels[planType]} ${paperFormat} ${orientationLabels[orientation]}`

  const {
    mutate: downloadDefaultTemplate,
    isLoading: isDownloadingDefault,
    variables: downloadVariables,
  } = useMutation(
    async (configuration: PlanTemplateConfiguration) => {
      const data = await getDefaultPlanTemplate.request(
        configuration.planType,
        configuration.paperFormat,
        configuration.orientation,
      )
      return { data, configuration }
    },
    {
      onSuccess: ({ data, configuration }) => {
        downLoadFile(data, 'application/pdf', `${templateFileName(configuration)}.pdf`)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Herunterladen der Standardvorlage'), {
          variant: 'error',
        })
      },
    },
  )

  const {
    mutate: uploadTemplate,
    isLoading: isUploading,
    variables: uploadVariables,
  } = useMutation(
    async ({ configuration, file }: { configuration: PlanTemplateConfiguration; file: File }) => {
      const existingTemplate = findCustomPlanTemplate(customTemplates, configuration)

      if (existingTemplate) {
        return updateCustomerFileContent.request(existingTemplate.guid, file)
      }

      const additionalData: PlanTemplateAdditionalData = {
        kind: planTemplateKind,
        ...configuration,
      }

      return saveCustomerFile.request({
        name: templateFileName(configuration),
        additional_data: additionalData,
        file,
      })
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(templatesQueryKey)
        enqueueSnackbar('Planvorlage erfolgreich hochgeladen', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hochladen der Planvorlage'), {
          variant: 'error',
        })
      },
    },
  )

  const {
    mutate: deleteTemplate,
    isLoading: isDeleting,
    variables: deleteVariables,
  } = useMutation(
    async ({
      customTemplate,
    }: {
      configuration: PlanTemplateConfiguration
      customTemplate: CustomerFile<PlanTemplateAdditionalData>
    }) => deleteCustomerFile.request(customTemplate.guid),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(templatesQueryKey)
        enqueueSnackbar('Eigene Planvorlage entfernt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Entfernen der Planvorlage'), {
          variant: 'error',
        })
      },
    },
  )

  if (isLoadingTemplates) {
    return <LoadingIndicator />
  }

  return (
    <Stack spacing={3} data-cy="plan-template-manager">
      <Typography variant="body2" color="text.secondary">
        {readOnly
          ? 'Hier finden Sie die Vorlagen für die PDF-Pläne (Logo, Beschriftung und Rahmen) sowie für das Statikdokument (Kopf- und Fußzeile). Sie können die Standardvorlage sowie eine angepasste Vorlage herunterladen. Angepasste Vorlagen werden bei der Erstellung automatisch anstelle der Standardvorlagen verwendet.'
          : 'Hier können die Vorlagen für die PDF-Pläne (Logo, Beschriftung und Rahmen) sowie für das Statikdokument (Kopf- und Fußzeile) angepasst werden. Laden Sie die Standardvorlage herunter, bearbeiten Sie diese und laden Sie sie wieder hoch. Angepasste Vorlagen werden bei der Erstellung automatisch anstelle der Standardvorlagen verwendet.'}
      </Typography>

      {planTemplateTypes.map(planType => (
        <Box key={planType}>
          <Typography variant="h6" mb={2}>
            {planTypeSectionLabels[planType]}
          </Typography>
          <TableContainer component={Paper}>
            <PlanTemplateTable
              planType={planType}
              customTemplates={customTemplates}
              onDownloadDefault={configuration => downloadDefaultTemplate(configuration)}
              downloadingConfiguration={isDownloadingDefault ? downloadVariables : undefined}
              onUpload={(configuration, file) => uploadTemplate({ configuration, file })}
              uploadingConfiguration={isUploading ? uploadVariables?.configuration : undefined}
              onDelete={(configuration, customTemplate) =>
                deleteTemplate({ configuration, customTemplate })
              }
              deletingConfiguration={isDeleting ? deleteVariables?.configuration : undefined}
            />
          </TableContainer>
        </Box>
      ))}

      <DocumentationTemplatesSection />
    </Stack>
  )
}

export default PlanTemplateManager
